<template>
  <v-dialog
    :value="value"
    content-class="crud-dialog"
    persistent
    max-width="560"
    @input="$emit('input', $event)"
  >
    <v-card class="crud-card">
      <v-card-title class="crud-card-title">
        <v-row no-gutters>
          <v-col cols="11" class="text-left">
            <h2 class="text-h5">Upload {{ documentTypeDisplayName }}</h2>
          </v-col>
          <v-col cols="1" class="text-right">
            <v-icon class="close" color="black" @click.stop="close">{{
              icons.mdiClose
            }}</v-icon>
          </v-col>
        </v-row>
      </v-card-title>
      <v-divider />
      <v-card-text class="crud-card-text">
        <p v-if="description" class="dialog-description">{{ description }}</p>

        <div v-if="requiresTitle" class="mb-3">
          <TextField
            v-model="title"
            label="Title"
            name="title"
            placeholder="Add a document title"
            hint="* Mandatory"
            :required="true"
            :errors="['* Mandatory']"
          />
        </div>

        <div
          class="dropzone"
          :class="{ 'dropzone--active': dragActive }"
          @click="openFilePicker"
          @dragover.prevent="dragActive = true"
          @dragenter.prevent="dragActive = true"
          @dragleave.prevent="dragActive = false"
          @drop.prevent="onDrop"
        >
          <DocumentIconBadge icon="upload" tile />
          <div class="dropzone-text mt-3">
            Drag &amp; drop files here, or
            <a class="browse-link" @click.stop="openFilePicker">browse</a>
          </div>
          <div class="text-caption text--secondary mt-1">
            PDF, JPG or PNG · Up to 30 MB each
          </div>
        </div>

        <input
          ref="fileInput"
          type="file"
          class="d-none"
          :multiple="allowMultipleFiles"
          :accept="accept"
          @change="onFileInputChange"
        />

        <div v-if="files.length" class="file-previews mt-3">
          <div
            v-for="(file, idx) in files"
            :key="`${file.name}-${idx}`"
            class="file-preview d-flex align-center"
          >
            <DocumentIconBadge icon="description" class="mr-3" />
            <div class="file-preview-meta flex-grow-1">
              <div class="filename text-body-2 font-weight-bold text-truncate">
                {{ file.name }}
              </div>
              <div class="d-flex align-center mt-1">
                <v-progress-linear
                  :value="busy ? progress : 0"
                  color="primary"
                  height="6"
                  rounded
                  class="flex-grow-1"
                />
                <span class="text-caption text--secondary ml-2 progress-text">
                  {{ busy ? `${progress}%` : '' }}
                </span>
              </div>
            </div>
            <v-btn
              icon
              small
              :disabled="busy"
              class="ml-1"
              @click="removeFile(idx)"
            >
              <v-icon size="18">{{ icons.mdiClose }}</v-icon>
            </v-btn>
          </div>
        </div>

        <p v-if="overallError" class="error--text mt-3 mb-0 text-body-2">
          {{ overallError }}
        </p>
      </v-card-text>
      <v-card-actions class="crud-card-actions">
        <v-spacer />
        <GradientButton
          title="Cancel"
          callout=""
          :small="true"
          button-size="medium"
          :fill="false"
          :disabled="busy"
          @gradientButtonClicked="close"
        />
        <GradientButton
          class="ml-2"
          title="Upload"
          callout=""
          :small="true"
          button-size="medium"
          data-test="submit-upload"
          :disabled="!canSubmit || busy"
          :loading="busy"
          @gradientButtonClicked="submit"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  import { mdiClose } from '@mdi/js';
  import { testatorDocumentUploaderMixin } from '../../mixins/testator-document-uploader';
  import { ALLOWED_MIME_TYPES } from '../../constants/js/testator-documents';
  import TextField from '../common/components/TextField.vue';
  import DocumentIconBadge from './DocumentIconBadge.vue';
  import GradientButton from '../guest/GradientButton.vue';

  export default {
    name: 'UploadDocumentDialog',
    components: { TextField, DocumentIconBadge, GradientButton },
    mixins: [testatorDocumentUploaderMixin],
    props: {
      value: { type: Boolean, default: false },
      viewedTestatorId: { type: [Number, String], required: true },
      documentType: { type: String, required: true },
      documentTypeDisplayName: { type: String, required: true },
      description: { type: String, default: '' },
      requiresTitle: { type: Boolean, default: false },
      allowMultipleFiles: { type: Boolean, default: false },
      // If provided, files are appended to this existing document via add_files.
      // If null, a new document is created.
      existingDocumentId: { type: [Number, String], default: null },
      accept: {
        type: String,
        default:
          '.pdf,.jpg,.jpeg,.png,.heic,.heif,application/pdf,image/jpeg,image/png,image/heic,image/heif',
      },
    },
    data() {
      return {
        icons: { mdiClose },
        files: [],
        title: '',
        progress: 0,
        busy: false,
        overallError: null,
        dragActive: false,
      };
    },
    computed: {
      canSubmit() {
        if (!this.files.length) return false;
        if (this.requiresTitle && !(this.title || '').trim()) return false;
        return true;
      },
    },
    methods: {
      close() {
        if (this.busy) return;
        this.$emit('input', false);
        this.reset();
      },
      reset() {
        this.files = [];
        this.title = '';
        this.progress = 0;
        this.overallError = null;
        this.dragActive = false;
      },
      openFilePicker() {
        if (this.busy) return;
        this.$refs.fileInput.click();
      },
      onFileInputChange(event) {
        this.addFiles(event.target.files);
        event.target.value = '';
      },
      onDrop(event) {
        this.dragActive = false;
        if (this.busy) return;
        this.addFiles(event.dataTransfer.files);
      },
      addFiles(fileList) {
        const incoming = Array.from(fileList || []).filter(f =>
          ALLOWED_MIME_TYPES.includes(f.type)
        );
        if (!incoming.length) return;
        this.files = this.allowMultipleFiles
          ? [...this.files, ...incoming]
          : incoming.slice(0, 1);
      },
      removeFile(idx) {
        if (this.busy) return;
        this.files = this.files.filter((_, i) => i !== idx);
      },
      async submit() {
        if (!this.canSubmit || this.busy) return;

        this.busy = true;
        this.overallError = null;
        this.progress = 0;

        const onProgress = percent => {
          this.progress = percent;
        };

        const result = this.existingDocumentId
          ? await this.addFilesToDocument({
              documentId: this.existingDocumentId,
              files: this.files,
              onProgress,
            })
          : await this.uploadFilesAsNewDocument({
              testatorId: this.viewedTestatorId,
              documentType: this.documentType,
              name: this.requiresTitle ? this.title : null,
              files: this.files,
              onProgress,
            });

        this.busy = false;

        if (result.error) {
          this.overallError = this.errorMessageForCode(result.error);
          return;
        }

        this.$emit('uploaded', { document: result.document });
        this.close();
      },
      errorMessageForCode(code) {
        switch (code) {
          case 'INVALID_FILE_TYPE':
            return 'Unsupported file type. Use PDF, JPG, PNG or HEIC.';
          case 'FILE_TOO_LARGE':
            return 'File is larger than 30MB.';
          case 'DOCUMENT_TYPE_ALREADY_EXISTS':
            return 'A document of this type already exists.';
          case 'INVALID_DOCUMENT_TYPE':
            return 'Unknown document type.';
          case 'UNAUTHENTICATED':
            return 'You are not signed in.';
          case 'FORBIDDEN':
            return 'You do not have permission to upload to this testator.';
          case 'S3_UPLOAD_FAILED':
            return 'Upload to S3 failed. Please try again.';
          default:
            return 'Upload failed.';
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import '../../constants/styles/modal.scss';
  @import '../../constants/styles/document-icon-badge.scss';

  .dialog-description {
    font-size: 14px;
    line-height: 20px;
    color: #6b7280;
    margin-bottom: 16px;
  }
  .close {
    cursor: pointer;
    transition: color 0.15s;
  }
  .close:hover {
    color: var(--omk-colour-icon-active-hover, #20491d) !important;
  }
  .dropzone {
    border: 1.5px dashed #b7d8c2;
    border-radius: 12px;
    background: #f1faf3;
    padding: 28px 16px;
    text-align: center;
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s;
  }
  .dropzone:hover,
  .dropzone--active {
    border-color: var(--v-primary-base);
    background: #e8f5ee;
    @include doc-badge-hover;
  }
  .dropzone-text {
    font-weight: 600;
    color: #111827;
  }
  .browse-link {
    color: var(--v-primary-base);
    text-decoration: underline;
    cursor: pointer;
    font-weight: 600;
    transition: color 0.15s;
  }
  .browse-link:hover {
    color: var(--omk-colour-icon-active-hover, #20491d);
  }
  .file-previews {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .file-preview {
    padding: 10px 12px;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    background: #fff;
  }
  .file-preview-meta {
    min-width: 0;
  }
  .file-preview-meta .filename {
    color: #111827;
  }
  .progress-text {
    min-width: 36px;
    text-align: right;
  }
</style>
